import type { ErrorDetails } from "schemas/errors";

import { toast } from "@replohq/lattice/components/toast/Toast";
import { analytics } from "@v2/features/infra/analytics/tracking";
import { modalStore } from "@v2/store/modalStore";
import { getMostRecentViewingUser } from "@v2/trpc/routes/user";
import { docs } from "@v2/utils/docs";
import { getPublisherUrl, SHOPIFY_APP_LISTING_URL } from "@v2/utils/env";
import { getErrorDetails } from "@v2/utils/get-error-details";
import { routes } from "@v2/utils/router";
import { generatePath } from "react-router-dom";
import { isLocalhost } from "replo-runtime/shared/env";
import { filterNulls } from "replo-utils/lib/array";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { PENDING_SHOPIFY_INTEGRATION_COOKIE } from "schemas/cookies";
import { errorUserFacingDetailsSchema, extractErrorId } from "schemas/errors";
import { z } from "zod";

function extractProjectIdFromUrl(): string | undefined {
  const match =
    window.location.pathname.match(/\/shops\/([^/]+)/) ??
    window.location.pathname.match(/\/editor\/([^/]+)/);
  return match?.[1];
}

function installShopifyFromUrl() {
  const projectId = extractProjectIdFromUrl();
  // Note (Ben, 2026-02-25): We read workspaceId from localStorage rather than
  // the URL because Shopify install errors typically occur on /shops/:projectId
  // routes which don't contain a /workspace/ segment.
  const workspaceId =
    localStorage.getItem("replo.workspace.lastvisitedWorkspaceId") ?? undefined;

  if (projectId && workspaceId) {
    const cookieValue = JSON.stringify({
      type: "noShopifyErrorModal",
      projectId,
      workspaceId,
    });
    const domain = getPublisherUrl().includes("replo.app")
      ? "; domain=replo.app"
      : "";
    document.cookie = `${PENDING_SHOPIFY_INTEGRATION_COOKIE}=${encodeURIComponent(cookieValue)}; path=/; max-age=600; samesite=lax${domain}`;
  }

  window.open(SHOPIFY_APP_LISTING_URL, "_blank");
}

function getToastActionForCallToAction(
  callToAction: ErrorDetails["callToAction"],
): { label: string; onClick: () => void } | undefined {
  if (!callToAction) {
    return undefined;
  }

  return exhaustiveSwitch(callToAction)({
    installApp: () => ({
      label: "Install Shopify App",
      onClick: installShopifyFromUrl,
    }),
    callback: (data) => ({
      label: data.name,
      onClick: data.onClick,
    }),
    link: (data) => ({
      label: data.name,
      onClick: () => {
        if (data.target === "_blank") {
          window.open(data.to, "_blank");
        } else {
          window.location.href = data.to;
        }
      },
    }),
    closeModal: () => undefined,
    navigate: (data) => {
      return exhaustiveSwitch(data.location)({
        integrationHub: () => ({
          label: "View Integrations",
          onClick: () => {
            window.location.href = generatePath(
              routes.shops.settings.shops.integrations.home,
              { projectId: extractProjectIdFromUrl() },
            );
          },
        }),
        home: () => ({
          label: "Go Home",
          onClick: () => {
            window.location.href = routes.home.root;
          },
        }),
        workspace: (locationData) => ({
          label: `Go to "${locationData.workspaceName}"`,
          onClick: () => {
            window.location.href = generatePath(
              routes.workspace.settings.general,
              { workspaceId: locationData.workspaceId },
            );
          },
        }),
      });
    },
    custom: (data): { label: string; onClick: () => void } | undefined => {
      if (!data.callToActionType) {
        return undefined;
      }
      return exhaustiveSwitch(data.callToActionType)({
        closeModal: () => undefined,
        reload: () => ({
          label: "Refresh",
          onClick: () => window.location.reload(),
        }),
        installApp: () => ({
          label: "Install Shopify App",
          onClick: installShopifyFromUrl,
        }),
        goToHomepage: () => ({
          label: "Go Home",
          onClick: () => {
            window.location.href = routes.home.root;
          },
        }),
        navigateToProjectEditor: () => ({
          label: "Go back to project",
          onClick: () => {
            window.location.href = generatePath(routes.editor.project, {
              projectId: extractProjectIdFromUrl(),
            });
          },
        }),
        navigateToLink: (linkData) => {
          return exhaustiveSwitch(linkData.link)({
            "errors.chunkingError": () => ({
              label: "Learn more",
              onClick: () => window.open(docs.errors.chunkingError, "_blank"),
            }),
            "errors.wrongJsonExtension": () => ({
              label: "Learn more",
              onClick: () => {
                window.open(docs.errors.wrongJsonExtension, "_blank");
              },
            }),
            "errors.indexBackupError": () => ({
              label: "Learn more",
              onClick: () => {
                window.open(docs.errors.indexBackupError, "_blank");
              },
            }),
            "errors.shopifyIntegrationAlreadyAssigned": () => ({
              label: "Learn more",
              onClick: () => {
                window.open(
                  docs.errors.shopifyIntegrationAlreadyAssigned,
                  "_blank",
                );
              },
            }),
            "errors.shopifyIntegrationMissingAccessScopes": (scopeData) => ({
              label: "Authorize",
              onClick: () => {
                window.open(scopeData.url, "_blank");
              },
            }),
            "errors.projectConnectedToBillingIntegrationForShopifyWorkspace":
              () => ({
                label: "Learn more",
                onClick: () => {
                  window.open(
                    docs.errors
                      .projectConnectedToBillingIntegrationForShopifyWorkspace,
                    "_blank",
                  );
                },
              }),
            "errors.themeAssetSizeLimitExceeded": () => ({
              label: "Learn more",
              onClick: () => {
                window.open(docs.errors.themeAssetSizeLimitExceeded, "_blank");
              },
            }),
            "errors.themeTotalSizeLimitExceeded": () => ({
              label: "Learn more",
              onClick: () => {
                window.open(docs.errors.themeTotalSizeLimitExceeded, "_blank");
              },
            }),
            "errors.pageMetafieldDefinitionsChanged": () => ({
              label: "Learn more",
              onClick: () => {
                window.open(
                  docs.errors.pageMetafieldDefinitionsChanged,
                  "_blank",
                );
              },
            }),
            "settings.billing": () => ({
              label: "Upgrade Plan",
              onClick: () => {
                window.location.href = "/billing";
              },
            }),
            customLink: (customLinkData) => ({
              label: "Learn more",
              onClick: () => window.open(customLinkData.url, "_blank"),
            }),
          });
        },
      });
    },
  });
}

/**
 * Handle non-tRPC errors that come through TanStack Query's global error
 * callbacks. Shows an "Unknown error" toast and tracks the event.
 */
export function handleNonTRPCClientError(error: unknown) {
  const message = error instanceof Error ? error.message : String(error);
  const errorId = extractErrorId(error);
  const description =
    message ||
    "An unknown error has occurred. If this error persists, please reach out to support@replo.app for help.";

  if (isLocalhost()) {
    const stack = error instanceof Error ? error.stack : undefined;
    const copyPayload = filterNulls([
      message,
      stack,
      errorId ? `errorId: ${errorId}` : undefined,
    ]).join("\n");
    toast.error({
      title: `Error: ${message}`,
      description: stack ?? description,
      duration: 30_000,
      action: {
        label: "Copy",
        onClick: () => void navigator.clipboard.writeText(copyPayload),
        dismissOnClick: false,
      },
    });
  } else {
    toast.error({ title: "Unknown error", description });
  }

  analytics.logEvent("error.unknown.shown", {
    errorId,
    errorSource: "non-trpc",
  });
}

/**
 * Handle the given error from TRPC (this triggers the standard toast/modal
 * processing).
 *
 * Note (Noah, 2024-12-23): The type here is hard to express, since TRPC
 * actually generates a TRPCErrorLike generic, but we only need the data here,
 * so this type is { data: unknown }
 */
export function handleTRPCClientError(error: {
  data?: { path: string; debugMessage?: string; errorId?: string } | null;
  meta?: { response?: { url?: string } } | null;
  shape?: { code: number; message?: string } | null;
}) {
  const result = z
    .object({ userFacingDetails: errorUserFacingDetailsSchema })
    .safeParse(error.data);
  const errorId = error.data?.errorId;
  const user = getMostRecentViewingUser();
  const details = getErrorDetails({
    type: "trpcError",
    userFacingDetails: result.success
      ? result.data.userFacingDetails
      : undefined,
    code: error.shape?.code,
    path: error.data?.path,
    url: error.meta?.response?.url,
    errorId,
    isSuperuser: user?.isSuperuser ?? false,
    errorMessage: error.data?.debugMessage ?? error.shape?.message,
  });
  if (details && details.type === "toast") {
    let description: string | undefined;
    if (Array.isArray(details.errorDetails.message)) {
      description = details.errorDetails.message.join(", ");
    } else if (typeof details.errorDetails.message === "string") {
      description = details.errorDetails.message;
    }
    toast.error({
      title: details.errorDetails.header,
      description,
      action: getToastActionForCallToAction(details.errorDetails.callToAction),
    });

    if (details.errorKey === "unknown") {
      analytics.logEvent("error.unknown.shown", {
        errorId,
        endpoint: error.data?.path ?? error.meta?.response?.url,
        errorSource: "trpc",
      });
    }
  }
  if (details && details.type !== "toast") {
    modalStore.getState().actions.handleErrorDetails({ details });
  }
}
